import React, { useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Icon } from 'components/blocks/menu-icon/menu-icon';
import { NotificationSideSheet } from './NotificationSideSheet';

import { useNotificationFeedStore } from 'state/store/notifications/use-notification-feed-store';
import { useNotificationListener } from 'hooks/use-notification-listener';

import { useTheme } from 'components/core/theme-provider';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { TooltipContent, Tooltip, TooltipTrigger, TooltipProvider } from 'components/ui/tooltip';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { isAgentShareFromCurrentUser } from '../utils/agent-share-notification';
import { resolveNotificationText } from '../utils/resolve-notification-text';
// import { useLocation } from 'react-router-dom';

interface NotificationBellProps {
    /** Sidebar placement uses a plain icon with no circular border. */
    borderless?: boolean;
    /** Header bell while the menu is collapsed. Stays 18px on large screens. */
    compact?: boolean;
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
    borderless = false,
    compact = false,
}) => {
    // const location = useLocation();
    const { t } = useTranslation();
    const { theme } = useTheme();
    const { toast } = useToast();
    const [isOpen, setIsOpen] = useState(false);
    const currentUserId = useCommonStore((state) => state.user?.id);

    const { fetchAndSetNotification, unseenCount, page } = useNotificationFeedStore();

    useEffect(() => {
        if (isOpen) {
            fetchAndSetNotification(page);
        }
    }, [isOpen, page]);

    // Read the count from the store at event time so back-to-back notifications are all counted.
    const incrementUnseenCount = () => {
        const feedStore = useNotificationFeedStore.getState();
        feedStore.setUnseenCount(feedStore.unseenCount + 1);
    };

    useNotificationListener('blocks-notification', (event) => {
        const rawPayload = (event as { message?: { denormalizedPayload?: unknown } })?.message
            ?.denormalizedPayload;
        if (!rawPayload) return;

        try {
            const payload = typeof rawPayload === 'string' ? JSON.parse(rawPayload) : rawPayload;

            // 1. Custom / Legacy payload structure (payload.Message)
            if (payload?.Message && payload.Message.type !== 'file_upload') {
                if (isAgentShareFromCurrentUser(payload, currentUserId)) {
                    return;
                }

                const verb = payload.Message.Verb || payload.Message.verb;
                const title = payload.Message.title || 'Notification';
                const description = payload.Message.description;
                const organizationName = payload.Message.displayName;

                incrementUnseenCount();
                if (payload.Message.type === 'COLLABORATION') {
                    return;
                }

                if (verb === 'REMOVE' || verb === 'FAILED' || verb === 'CANCEL') {
                    toast({
                        title: t(title) || title,
                        description: t(description, { organizationName: `${organizationName}` }) || description,
                        variant: 'destructive',
                        duration: 3000,
                    });
                } else if (verb === 'ADD' || verb === 'ACCEPT' || verb === 'CHANGE' || verb === 'SHARE') {
                    toast({
                        title: title,
                        description: t(description, { organizationName: `${organizationName}` }) || description,
                        variant: 'success',
                        duration: 3000,
                    });
                }
                return;
            }

            // 2. Standard Blocks-OS payload structure (title, description, toastable, isSuccess)
            if (payload?.title || payload?.description) {
                const resolved = resolveNotificationText(payload, t);
                // An empty resolved title (none sent, or an untranslated key with no English) shows the description instead.
                const title = resolved.title || resolved.description;
                const description = resolved.title ? resolved.description : undefined;
                const isFailure = payload.isSuccess === false || payload.status === 'FAILED';
                const shouldToast = payload.toastable !== false;

                incrementUnseenCount();

                if (shouldToast && title) {
                    toast({
                        title,
                        description,
                        variant: isFailure ? 'destructive' : 'success',
                        duration: 3000,
                    });
                }
                return;
            }

        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Error parsing notification payload:', err);
        }
    });

    const handleClick = () => {
        setIsOpen(true);
    };

    let iconColorClass = 'text-grey-600';
    if (theme === 'dark') iconColorClass = 'text-true-white';
    else if (compact) iconColorClass = 'text-grey-500';

    return (
        <>
            <Button
                variant="ghost"
                size="icon"
                onClick={handleClick}
                aria-label="View Notifications"
                aria-expanded={isOpen}
                data-buddy-icon="notifications"
                className={cn(
                    'relative flex shrink-0 cursor-pointer items-center justify-center self-center rounded-full p-0 transition-opacity hover:bg-transparent hover:opacity-80',
                    compact
                        ? 'h-7 w-7 2xl:h-8 2xl:w-8 2xl:p-1.5 [&_svg]:!size-[18px] 2xl:[&_svg]:!size-5'
                        : 'size-7 2xl:size-8 [&_svg]:size-[18px] 2xl:[&_svg]:size-6',
                    !borderless && (compact ? 'border-hairline' : 'border'),
                    !borderless &&
                        (theme === 'dark'
                            ? 'border-border bg-transparent'
                            : 'border-grey-200 bg-true-white')
                )}
            >
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div>
                                <Icon
                                    name="Bell"
                                    width={compact ? 20 : 24}
                                    height={compact ? 20 : 24}
                                    strokeWidth={isOpen ? 2 : 1.5}
                                    className={cn(
                                        'size-[18px]',
                                        compact ? '2xl:size-5' : '2xl:size-6',
                                        iconColorClass
                                    )}
                                />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>{t('NOTIFICATIONS') || 'Notifications'}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>

                {unseenCount > 0 && (
                    <div className="absolute -right-1 -top-3 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-primary px-1 text-3xs font-semibold leading-none text-destructive-foreground">
                        {unseenCount > 99 ? '99+' : unseenCount}
                    </div>
                )}
            </Button>
            <NotificationSideSheet isOpen={isOpen} onClose={() => setIsOpen(false)} />
        </>
    );
};

